import React, { useState } from "react";
import { initalColors } from "./lib/utilty";

const PieChart = ({
  data = [], // This corresponds to dataSets in App.js, each item should have { name, data, color }
  size = 200,
  title = "",
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hiddenSlices, setHiddenSlices] = useState([]);

  // Calculate total value for the visible slices
  const total = data
    .filter((_, index) => !hiddenSlices.includes(index))
    .reduce((acc, item) => acc + item.value, 0);

  const toPercentage = (value) => (value / total) * 100;

  // Helper function to generate the path data for each slice
  const createSlices = () => {
    let cumulativePercentage = 0;
    const visibleData = data.filter(
      (_, index) => !hiddenSlices.includes(index)
    );

    return visibleData.map((item, index) => {
      const percentage = toPercentage(item.value);

      if (percentage === 100) {
        // Special case for a single slice that covers the entire circle
        return (
          <circle
            key={index}
            cx={size / 2}
            cy={size / 2}
            r={size / 2}
            fill={
              item.color ||
              initalColors[data.indexOf(item) % initalColors.length]
            } // Use item.color
            className={`transition-opacity duration-300 ${
              hoveredIndex === data.indexOf(item) ? "opacity-75" : "opacity-100"
            }`}
            onMouseEnter={() => setHoveredIndex(data.indexOf(item))}
            onMouseLeave={() => setHoveredIndex(null)}
          />
        );
      }

      const [startX, startY] =
        getCoordinatesForPercentage(cumulativePercentage);
      cumulativePercentage += percentage / 100;
      const [endX, endY] = getCoordinatesForPercentage(cumulativePercentage);

      const largeArcFlag = percentage > 50 ? 1 : 0;

      return (
        <path
          key={index}
          d={`M ${size / 2} ${size / 2} L ${startX} ${startY} A ${size / 2} ${
            size / 2
          } 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
          fill={
            item.color || initalColors[data.indexOf(item) % initalColors.length]
          } // Use item.color
          className={`transition-opacity duration-300 ${
            hoveredIndex === data.indexOf(item) ? "opacity-75" : "opacity-100"
          }`}
          onMouseEnter={() => setHoveredIndex(data.indexOf(item))}
          onMouseLeave={() => setHoveredIndex(null)}
        />
      );
    });
  };

  // Helper function to calculate coordinates for percentage position on circle
  const getCoordinatesForPercentage = (percentage) => {
    const x = Math.cos(2 * Math.PI * percentage) * (size / 2) + size / 2;
    const y = Math.sin(2 * Math.PI * percentage) * (size / 2) + size / 2;
    return [x, y];
  };

  // Calculate the center of each slice for positioning the tooltip
  const getSliceCenter = (index) => {
    const startPercentage =
      data
        .filter((_, i) => !hiddenSlices.includes(i))
        .slice(0, index)
        .reduce((acc, item) => acc + toPercentage(item.value) / 100, 0) +
      toPercentage(data[index].value) / 200;

    return getCoordinatesForPercentage(startPercentage);
  };

  // Toggle the visibility of slices when legend items are clicked
  const toggleSliceVisibility = (index) => {
    setHiddenSlices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <div className="relative flex flex-col items-center">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {createSlices()}
      </svg>
      <div className="mt-4 flex flex-col items-center">
        {data.map((item, index) => (
          <div
            key={index}
            className={`flex items-center space-x-2 cursor-pointer w-full ${
              hiddenSlices.includes(index) ? "opacity-50" : ""
            }`}
            onClick={() => toggleSliceVisibility(index)}
          >
            <div
              className="w-4 h-4"
              style={{
                backgroundColor:
                  item.color || initalColors[index % initalColors.length],
              }} // Use item.color
            ></div>
            <span className="text-sm text-gray-700">
              {item.label}{" "}
              {/* item.label comes from the mapping in index.jsx */}
              {!hiddenSlices.includes(index) && (
                <span>{toPercentage(item.value).toFixed(2)}%</span>
              )}
            </span>
          </div>
        ))}
      </div>
      {hoveredIndex !== null && !hiddenSlices.includes(hoveredIndex) && (
        <div
          className="absolute bg-white text-gray-700 text-sm px-2 py-1 rounded shadow-lg pointer-events-none"
          style={{
            top: `${getSliceCenter(hoveredIndex)[1]}px`,
            left: `${getSliceCenter(hoveredIndex)[0]}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          {data[hoveredIndex].label}: {data[hoveredIndex].value}
        </div>
      )}
    </div>
  );
};

export default PieChart;
